---
title: Validation Console
sidebar_position: 4
description: Validation Console
---

# Validation Console

The **Validation Console** enables users to organize, manage, and complete expert review tasks using package bundles for validation and training purposes.

## Overview

**Purpose**:

- Collect real-world observations from field visits
- Validate satellite-based predictions
- Provide training data for marker algorithms
- Support compliance verification
- Organize expert review tasks into structured packages
- Track task progress through status phases
- Enable systematic field validation and compliance review

**Key Components**:

- **Bundles**: Named groups of task packages built from FOI filters and input definitions
- **Packages**: Individual task assignments for experts; each package covers a subset of the bundle's FOIs
- **Validation View**: Interface for completing assigned reviews
- **Reports** (scope-dependent): Generate a PDF report for selected observations

**Status Tracking** - Packages move through phases:

1. **Unassigned**: Available for assignment
2. **Processing**: Allocated to an expert, work in progress
3. **Finished**: Review complete (the underlying API status value is `COMPLETED`; this matters when filtering packages by status)

**Permissions**:
Bundle Creation requires **GTC-ADMIN** role. Bundles View, Packages View, and Validation View require at minimum **GTC-VIEWER** (read/view only) or **GTC-OPERATOR** (write - providing user inputs) access.

## Bundle Creation View

**Access**: Navigate via the collapsible side panel menu — **Bundle Creation**. Requires **GTC-ADMIN** role.

![Bundle Creation module interface](../imgs/bundle-create.png)

A bundle defines what FOIs go into packages and what inputs experts must fill in for each FOI. The **Create** button is disabled until at least one filter is added.

### Step 1 — Set bundle name and package size

1. Enter a **Bundle name** to identify the bundle later.
2. Set **Package size** — the maximum number of FOIs in a single package. The system creates `ceil(FOI count / package size)` packages.

**Hard limits: max 1000 FOIs per package and max 1000 packages per bundle.** If either value would exceed 1000, the bundle fails immediately with status **FAILED** — no packages are created. For example, setting package size to 2000 for 3000 matched FOIs would produce 2 packages of 2000, both over the per-package limit, and the bundle fails. See [Bundle status: FAILED](07-support-troubleshooting.mdx#issue-bundle-creation-status-is-failed) for the full resolution steps.

### Step 2 — Define input definitions

Input definitions determine what form fields appear in the Validation View for each FOI for users to provide their input. Add as many inputs as needed. For review only purpuses where no user input is required, you can skip this step and create a bundle with no inputs - this is useful if one needs a collection of FOIs based on (mostly marker) queries or for generating PDF reports without requiring expert input.

1. Select an input type:

| Type               | Fields                                                                                                                                                                                                                             |
| ------------------ | ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| **Note**           | **Label** (required);<br />**Required** checkbox                                                                                                                                                                                   |
| **Classification** | **Label** (required);<br />two or more **options**, each with a label and a 1-character **keyboard shortcut**;<br />**Multiselect** flag, enabling users to select more than just one option from list;<br />**Required** checkbox |
| **Observation**    | **Label** (required);<br />1-character **keyboard shortcut**;<br />1-3 character **acronym**;<br />`#RRGGBB` **colour**                                                                                                            |
| **Event**          | **Label** (required);<br />1-character **keyboard shortcut**;<br />1-3 character **acronym**;<br />`#RRGGBB` **colour**                                                                                                            |

2. Fill in the fields for the selected type.
3. Click **Add** to add the input definition to the bundle.
4. Repeat for each additional input.

### Step 3 — Set bundle filters

Filters determine which FOIs are included. All filters are combined with **AND** operator. At least one filter is required before the bundle can be created.

#### FOI List

1. Enter FOI IDs as a comma- or whitespace-separated list. Each ID must be in `<SCOPE>.FOI.<ID>` format.
2. Click **Add filter**.

Example:

```
DEMO_FR23.FOI.92289506, DEMO_FR23.FOI.92289498, DEMO_FR23.FOI.92289496, DEMO_FR23.FOI.92289493, DEMO_FR23.FOI.92289485, DEMO_FR23.FOI.92289473, DEMO_FR23.FOI.92289471, DEMO_FR23.FOI.92289466, DEMO_FR23.FOI.92289457, DEMO_FR23.FOI.92289420
```

#### FOI Query

1. Select a **FOI dataset**. A FOI dataset is exported reference data that was used for signal and marker calculation. Each dataset:
   - covers a specific time window, and
   - holds a _snapshot_ of the FOIs and their attributes as they were at the time of export.

   Because it's a snapshot, filtering by an attribute applies to the values _as captured in that dataset_, not necessarily the current values in the system. For scopes with a single reference-data import, the choice doesn't matter. For scopes with multiple imports (and therefore multiple FOI datasets), pick the dataset whose time window best matches your filtering criteria — usually the most recent one.

2. Select an **attribute** to filter by. Available attributes include all standard FOI attributes `id`, `ref_id`, `parent_id`, `foi_type`, `holding_id`, `crop_id`, `land_use_id`, `geometry`, `elevation`, `slope`, `orientation`, `area`, `perimeter`, `centroid`, `soil_type_id`, `agricultural_practice_id` and the dataset's own additional attributes.

3. Select an **operation**: `EQUALS`, `NOT_EQUALS`, `LESS_THAN`, `GREATER_THAN`, or `IN` (multi-value).

4. Enter a **value** (or multiple values for `IN`). The input type depends on the attribute's type:
   - For string attributes, it's a text input of exact string value.
   - For numeric attributes, it's a number input (see paragraph bellow for more details).
   - For geometry, it's a WKT string (e.g., `POLYGON((...))`).
   - For codelists, it's selection in a searchable dropdown of the codelist entries (see paragraph bellow for more details).

5. Click **Add filter**.

**Units for the measurement attributes** (enter plain numbers in these units):

| Attribute     | Unit                                                            |
| ------------- | --------------------------------------------------------------- |
| `area`        | square metres (m²) — e.g. a 2 ha parcel ≈ `20000`               |
| `perimeter`   | metres (m)                                                      |
| `elevation`   | metres (m), FOI average                                         |
| `slope`       | percent (%), FOI average                                        |
| `orientation` | degrees (°), azimuth — 0° = North, increasing clockwise (0-360) |

**Filtering codelist attributes by code:**
When you pick a codelist-backed attribute (`foi_type`, `crop_id`, `land_use_id`, `soil_type_id`, or `agricultural_practice_id`), the **value** field becomes a searchable **drop-down** of that codelist's entries, shown with their human-readable labels (you no longer need the fully-qualified ID such as `DEMO_FR23.CL_CROP.VRC`). Type to filter the list by either the label or the short code (e.g. `VRC`), and for the `IN` operation you can pick several values. Typing a known short code resolves to the correct entry automatically.

#### Marker Query

1. Select a **marker context** (see [Key Concepts](01-introduction.mdx#marker-context)). The context determines which marker calculations are used for filtering.

2. Select a **marker type** (the system resolves the inference automatically).

3. Select a marker **attribute**

4. Select an **operation**: `EQUALS`, `NOT_EQUALS`, `LESS_THAN`, `GREATER_THAN`, or `IN` (multi-value).

5. Enter a **value**
   - For string attributes, it's a text input of exact string value.
   - For numeric attributes, it's a number input (see paragraph bellow for more details).

6. Click **Add filter**.

**How marker-attribute filters are evaluated?** The filter runs against the inference dataset that corresponds to the selected **marker context** and **marker type**. That dataset covers a specific time window — the same context and time range shown in the FOI View or Validation View **Markers Summary** tab when you have that context active. This means the count a filter matches is the same count visible in the Markers Summary **for that context**. If filter was created with marker context different to the one selected in scope options, marker results displayed in FOI View or Validation View Markers Summary tab will not necesarily match the filter results.

Example: if you filter on a bare-soil marker's observation-count attribute for a value of `2`, the bundle includes only FOIs that have exactly two bare-soil detections within the inference's time window for the selected context — matching what you would see in **FOI View Markers Summary** for that context.

##### Marker attributes by marker type

Which attributes appear in the **attribute** dropdown depends on the **marker type's family** (group). The names shown in the dropdown are human-readable labels — the marker service maps each marker's raw output column to a label, so you don't work with raw column names directly. The tables below list, per family, the attributes you'll typically filter on, what they mean, and the values they hold.

Every marker type also exposes three system fields — **FOI ID**, **Marker ID**, and **Status** (`0` = OK, `1` = execution failed, `2` = not executed, `3` = executed but nothing detected, for event markers). You normally filter on the marker-specific attributes below rather than these.

**Aggregated-value markers** (e.g. Mean NDVI)

| Attribute    | Represents                                                   | Values |
| ------------ | ------------------------------------------------------------ | ------ |
| Marker value | The aggregated signal value over the period (e.g. mean NDVI) | number |

**Bare soil**

| Attribute                        | Represents                                           | Values  |
| -------------------------------- | ---------------------------------------------------- | ------- |
| Observation Count                | Total number of bare-soil observations in the period | integer |
| Marker Observations - Confidence | Per-observation bare-soil probability                | 0-1     |
| Marker Observations - Date       | Date of a bare-soil observation                      | date    |

**Count markers** (e.g. number of mowing events)

| Attribute | Represents                             | Values  |
| --------- | -------------------------------------- | ------- |
| Count     | Number of detected events/observations | integer |

**Classification-style markers** — Crop group, Land cover group, Crop classification, Similarity, Distance

| Attribute                                       | Represents                                                 | Values     |
| ----------------------------------------------- | ---------------------------------------------------------- | ---------- |
| Classified As                                   | Predicted / most-similar class code                        | class code |
| Declared As                                     | Declared class code (not present for Land cover group)     | class code |
| Score of the classification                     | Confidence of the predicted class                          | 0-100      |
| Marker Scores - Hypothesis                      | A candidate class being scored                             | class code |
| Marker Scores                                   | Score of a given hypothesis class                          | 0-100      |
| Num. of FOIs with the same crop (Distance only) | Neighbours with the same declared crop used for comparison | integer    |

**Homogeneity**

| Attribute                                  | Represents                         | Values                          |
| ------------------------------------------ | ---------------------------------- | ------------------------------- |
| Classified As                              | FOI-level result                   | `homogeneous` / `heterogeneous` |
| Score of the classification                | Confidence of the result           | 0-100                           |
| Marker Scores - Hypothesis / Marker Scores | Per-hypothesis label and its score | label / 0-100                   |

**Decisions (traffic-light)**

| Attribute                           | Represents                                   | Values |
| ----------------------------------- | -------------------------------------------- | ------ |
| Decisions decision code             | The decision outcome code                    | code   |
| Decisions traffic light colour code | Assigned colour (e.g. green/yellow/red)      | code   |
| Decisions lane code                 | Which decision lane/path produced the result | code   |

**Evaluation**

| Attribute  | Represents                                       | Values       |
| ---------- | ------------------------------------------------ | ------------ |
| Evaluation | Whether the FOI passes the evaluation expression | true / false |

**Event markers** — Mowing, Ploughing, and similar

| Attribute                                       | Represents                                     | Values  |
| ----------------------------------------------- | ---------------------------------------------- | ------- |
| Start date of the event / End date of the event | When the detected event begins / ends          | date    |
| Marker Duration                                 | Event length                                   | days    |
| Marker Observations Count                       | Valid observations within the event window     | integer |
| Start NDVI / End NDVI / Bottom NDVI             | NDVI at the event start, end, and lowest point | number  |
| NDVI Drop                                       | Magnitude of the NDVI decrease                 | number  |

Generic event markers additionally expose detailed timestamps and per-point values (event/threshold/extrema timestamps and their FEATURE values, and an event probability) for fine-grained queries.

**Merged mowing**

| Attribute                          | Represents                                     | Values         |
| ---------------------------------- | ---------------------------------------------- | -------------- |
| Start date / End date of the event | Merged event window                            | date           |
| Event probability                  | Confidence of the merged event                 | number         |
| Input count / Inputs               | How many source markers were merged, and which | integer / text |

**Greening & harvest**

| Attribute                                             | Represents                                      | Values |
| ----------------------------------------------------- | ----------------------------------------------- | ------ |
| Start/End date of greening; Start/End date of harvest | Greening and harvest event windows              | date   |
| Greening duration / Harvest duration                  | Length of each phase                            | days   |
| Greening/Harvest start NDVI, end NDVI, NDVI drop      | NDVI at phase boundaries and the drop magnitude | number |

**Pixel-level mowing**

| Attribute                                                       | Represents                                       | Values        |
| --------------------------------------------------------------- | ------------------------------------------------ | ------------- |
| Mowing detected                                                 | Whether any mowing was detected                  | true / false  |
| Number of pixels with mowing detected                           | Absolute count of mown pixels                    | integer       |
| Share of pixels with mowing detected                            | Mown pixels as a fraction of the FOI             | 0-1           |
| Max number / Max share of connected pixels with mowing detected | Largest contiguous mown patch (count / fraction) | integer / 0-1 |
| Number of all FOI pixels                                        | Total pixels in the FOI                          | integer       |

**Pole clearing**

| Attribute                        | Represents                           | Values  |
| -------------------------------- | ------------------------------------ | ------- |
| Observation Count                | Total clearing detections            | integer |
| Marker Observations - Date       | Predicted clearing date              | date    |
| Marker Observations - Confidence | Probability the pole area is cleared | 0-1     |

### Step 4 — Create the bundle

1. Review all settings.
2. Click **Create**.

The bundle is created with status **CREATED**, transitions to **PROCESSING** while packages are generated, then reaches **DONE** (or **FAILED** on error). Packages are available as soon as the bundle reaches **DONE**.

## Bundles View

**Access**: Navigate via the collapsible side panel menu — **Bundles**.

![Bundles module showing list of bundles](../imgs/bundle-list.png)

The Bundles view lists all bundles in the scope. Columns include bundle name, creation date, number of packages, and bundle status. You can filter and sort the list.

**Actions per bundle**:

- **Edit name**: Rename the bundle.
- **Open**: Navigate to the Packages view filtered to this bundle.
- **Delete**: Delete the bundle, its packages, and all associated data. A confirmation dialog appears before deletion.

## Packages View

**Access**: Navigate via the collapsible side panel menu — **Packages**.

![Packages module interface](../imgs/packages-view.png)

The Packages view lists all packages across all bundles. Filter by status, assigned user, bundle ID, or package ID.

**Selecting packages**: Click a row to select it. Hold **CTRL** (or **CMD** on Mac) to add or remove individual rows — selections made this way persist across pages. Hold **SHIFT** to select a contiguous range; SHIFT range-select operates within the current page only (the anchor resets when you paginate).

**Actions available from the toolbar**:

- **Assign / Reassign**: Assign unassigned packages or reassign already-assigned packages to a selected user.
- **Export to CSV**: Export package data to a CSV file (one `.csv` file per selected package).
- **Delete**: Delete the package and all its collected data (user inputs). Confirm in the dialog before deleting.

**Per-row actions** (Actions column):

- **Open Package** (focus icon): Open the package directly in the Validation View.

## Validation View

**Access**: Navigate via the collapsible side panel menu — **Validation View**, or open a package directly from the Packages view.

**Note**: The Validation View shares components with the FOI View Module (see [FOI View Module](03-foi-view-module.mdx)) — the observations widget, map widget, and signal/marker visualization. This section covers the validation-specific workflow.

![Validation View](../imgs/validation-view.png)

The app header shows the current scope, **bundle ID**, and **package ID** while you are working inside a package. Clicking the **bundle ID** opens a dropdown with two options: **"See bundle in Bundles view"** (navigates to the Bundles view filtered to that bundle) and **"See bundle packages in Packages view"** (navigates to the Packages view filtered to that bundle's packages). Clicking the **package ID** opens a dropdown with one option: **"See package in Packages view"** (navigates to the Packages view filtered to that specific package).

### Selecting an FOI

1. Once a package is open, the left column shows the list of FOIs in that package.
2. Click an FOI in the list. It loads in the review interface.

### Examining data

For the selected FOI, review:

- **Observations**: Satellite imagery in the timeline
- **Markers**: Predictions and confidence values
- **Signals**: Signal time-series in the chart
- **Map**: Spatial context and neighboring FOIs

### Input form types

Below the FOI list, the left column shows the input forms defined for this bundle.

#### Note inputs

1. Type a note into the text box.
2. Click **Save**.

#### Classification inputs

1. Select one option from the classification dropdown (or multiple options if the input has **Multiselect** enabled, or use the keyboard shortcut defined for each option).
2. Click **Save**.

#### Observation inputs

1. Activate the input by clicking it in the form or pressing its keyboard shortcut.
2. In the observations widget, select one or more dates for the observation.
3. Click **Save**.

#### Event inputs

1. Activate the input by clicking it in the form or pressing its keyboard shortcut.
2. In the observations widget, select a start and end date for the event.
3. Click **Save**.

### Saving and submitting data

| Action   | Result                                               |
| -------- | ---------------------------------------------------- |
| **Save** | Saves entered data; FOI remains in _processing_      |
| **Done** | Saves data and marks the FOI as _completed_          |
| **Skip** | Marks the FOI as _skipped_ and moves to the next FOI |

### Completing a package

1. After reviewing all FOIs:
2. Click **Finish Package**.
3. Confirm completion in the dialog.
4. Package status changes to **Finished** (API value: `COMPLETED`) and is closed.

All FOIs must be reviewed (completed or skipped) before finishing. Missing decisions will be flagged.

## PDF Report

![Reports tab in the Validation View, showing the observation selection prompt, Evaluation identifier and Free text fields, and the GENERATE PDF button](../imgs/validation-console-reports-tab.png)

The **Reports** tab appears in the Validation View left column only when the scope has PDF reporting enabled (`reporting.pdfGeneration.show = true` in the scope configuration). If the tab is not visible, your scope does not have this feature active.

### Generating a report

1. In the Validation View, Click the **Reports** tab in the left column.
2. Select **1-6 observations** in the observations widget. As you select each one, a blue PDF icon appears on its observation tile and a blue vertical band appears on the signals/markers chart. An **N/6** counter chip in the Reports tab updates as you select.
3. Fill in the **Evaluation identifier** field (mandatory).
4. Optionally fill in the **Free text** field.
5. Click **GENERATE PDF**. The button is enabled only when the identifier is non-empty, 1-6 observations are selected, and no generation is already in progress. The PDF downloads automatically when ready.
